export const USERNAME_PATTERN = /^[a-z0-9][a-z0-9_-]{2,29}$/;

const RESERVED_USERNAMES = new Set([
  'admin',
  'api',
  'auth',
  'contact',
  'dashboard',
  'example',
  'help',
  'login',
  'profile',
  'signup',
  'support',
  'www',
]);

export function normalizeUsername(username = '') {
  return username.trim().toLowerCase();
}

export function usernameError(username) {
  const normalized = normalizeUsername(username);
  if (!normalized) return 'Choose a username';
  if (!USERNAME_PATTERN.test(normalized)) return 'Use 3–30 lowercase letters, numbers, hyphens, or underscores';
  if (RESERVED_USERNAMES.has(normalized)) return 'This username is reserved';
  return '';
}

export function requiredProfileItems(creator) {
  return [
    { key: 'username', label: 'Choose a valid, unique username', complete: !usernameError(creator.username), path: '/dashboard/profile#public-profile-status' },
    { key: 'displayName', label: 'Add your display name', complete: Boolean(creator.displayName?.trim()), path: '/dashboard/profile#basic-info' },
    { key: 'headline', label: 'Add a professional headline', complete: Boolean(creator.headline?.trim()), path: '/dashboard/profile#basic-info' },
    { key: 'avatarUrl', label: 'Add a profile photo', complete: Boolean(creator.avatarUrl?.trim()), path: '/dashboard/profile#photos' },
    { key: 'location', label: 'Add your location', complete: Boolean(creator.location?.trim()), path: '/dashboard/profile#location-languages' },
  ];
}

export function isReadyToPublish(creator) {
  return requiredProfileItems(creator).every((item) => item.complete);
}

function optionalStatus(values, total) {
  const completed = values.filter(Boolean).length;
  if (completed === total) return 'Complete';
  if (completed > 0) return 'Needs attention';
  return 'Not started';
}

export function profileSections(creator, collections = {}) {
  const socials = collections.socials || [];
  const portfolio = collections.portfolio || [];
  const collaborations = collections.collaborations || [];
  const requiredItems = requiredProfileItems(creator);
  const usernameReady = requiredItems.find((item) => item.key === 'username')?.complete;
  const basicItems = requiredItems.filter((item) => ['displayName', 'headline'].includes(item.key));
  const firstMissingBasic = basicItems.find((item) => !item.complete);
  const profileImageReady = requiredItems.find((item) => item.key === 'avatarUrl')?.complete;
  const locationReady = requiredItems.find((item) => item.key === 'location')?.complete;
  return [
    {
      key: 'profileStatus',
      label: 'Publishing setup',
      path: '/dashboard/profile#public-profile-status',
      status: usernameReady && creator.published ? 'Complete' : 'Needs attention',
      hint: !usernameReady
        ? 'Choose a username before publishing.'
        : creator.published
          ? 'Your profile is live.'
          : 'Your profile is still a draft. Publish it to make it public.',
      required: true,
    },
    {
      key: 'profileBasics',
      label: 'Profile basic info',
      path: firstMissingBasic?.path || '/dashboard/profile#basic-info',
      status: basicItems.every((item) => item.complete) ? 'Complete' : 'Needs attention',
      required: true,
    },
    {
      key: 'profileImage',
      label: 'Profile image',
      path: '/dashboard/profile#photos',
      status: profileImageReady ? 'Complete' : 'Needs attention',
      required: true,
    },
    {
      key: 'location',
      label: 'Location',
      path: '/dashboard/profile#location-languages',
      status: locationReady ? 'Complete' : 'Needs attention',
      required: true,
    },
    {
      key: 'coverImage',
      label: 'Cover image',
      path: '/dashboard/profile#photos',
      status: creator.coverUrl?.trim() ? 'Complete' : 'Not started',
    },
    {
      key: 'contact',
      label: 'Availability & contact',
      path: '/dashboard/profile#availability-contact',
      status: optionalStatus([creator.availability?.trim(), creator.contactInfo?.trim()], 2),
    },
    {
      key: 'identity',
      label: 'Creator identity & niches',
      path: '/dashboard/profile#creator-identity',
      status: optionalStatus([creator.creatorTypes?.length, creator.niches?.length], 2),
    },
    { key: 'socials', label: 'Social stats', path: '/dashboard/socials', status: socials.length ? 'Complete' : 'Not started' },
    { key: 'portfolio', label: 'Content samples', path: '/dashboard/portfolio', status: portfolio.length ? 'Complete' : 'Not started' },
    {
      key: 'collaborations',
      label: 'Brand history',
      path: '/dashboard/collaborations',
      status: collaborations.length ? 'Complete' : 'Not started',
    },
  ];
}
